import type { CSSProperties } from "react"
import { Link } from "react-router-dom"
import { Shield, Users } from "lucide-react"
import type { GalleryTeam } from "../../hooks/useCccdTeamGallery"
import { TeamRoster } from "./TeamRoster"
import { categoryLabel, groupPhotoAlt, initials, institutionLine, memberCountLabel } from "./teamUtils"

const SIZES = "(min-width:1280px) 25vw, (min-width:1024px) 33vw, (min-width:640px) 50vw, 100vw"

const chip = "inline-flex items-center rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-semibold text-white"

export function TeamCard({
  team,
  eventId,
  index,
  animate,
}: {
  team: GalleryTeam
  eventId: string
  index: number
  animate: boolean
}) {
  const eager = index < 4
  const inst = institutionLine(team)
  const hasPhoto = !!team.groupPhotoUrl
  const category = categoryLabel(team.categorySlug)

  return (
    <article
      className={`group relative aspect-[4/3] overflow-hidden rounded-xl border border-border bg-muted transition-colors duration-[var(--dur-base)] hover:border-primary/50 focus-within:border-primary/50 motion-reduce:transition-none ${
        animate ? "motion-stagger-item" : ""
      }`}
      style={animate ? ({ "--i": Math.min(index, 7) } as CSSProperties) : undefined}
    >
      {hasPhoto ? (
        <img
          src={team.groupPhotoUrl!}
          alt={groupPhotoAlt(team)}
          sizes={SIZES}
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          className="absolute inset-0 h-full w-full origin-center object-cover object-[center_30%] transition-transform duration-[var(--dur-slow)] ease-[var(--ease-out-expo)] group-hover:scale-[1.03] group-focus-within:scale-[1.03] motion-reduce:transition-none motion-reduce:transform-none"
        />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-muted px-4 pb-24 pt-6">
          {team.patchImageUrl ? (
            <img
              src={team.patchImageUrl}
              alt={`Parche de ${team.name}`}
              loading={eager ? "eager" : "lazy"}
              decoding="async"
              className="h-24 w-24 object-contain"
            />
          ) : (
            <Shield className="h-16 w-16 text-muted-foreground/50" aria-hidden="true" />
          )}
          <div className="flex items-center gap-1" aria-hidden="true">
            {team.members.slice(0, 6).map((m) => (
              <span
                key={m.id}
                className="flex size-8 items-center justify-center rounded-full bg-card text-xs font-semibold text-foreground"
              >
                {initials(m.firstName, m.lastName)}
              </span>
            ))}
          </div>
          <p className="text-sm font-medium text-muted-foreground">Foto de equipo pendiente</p>
        </div>
      )}

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-black/75 via-black/40 to-transparent" />

      {/* Stretched link: whole card clickable without nesting controls */}
      <Link
        to={`/cccd/competidores/${eventId}/equipos/${team.id}`}
        aria-label={`Ver equipo ${team.name}`}
        className="absolute inset-0 z-10 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />

      {team.patchImageUrl && hasPhoto && (
        <div className="pointer-events-none absolute left-3 top-3 flex size-10 items-center justify-center overflow-hidden rounded-lg bg-card p-1">
          <img
            src={team.patchImageUrl}
            alt={`Parche de ${team.name}`}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-contain"
          />
        </div>
      )}

      <div
        className={`pointer-events-none absolute inset-x-0 z-[5] space-y-1.5 px-4 text-white ${
          team.members.length > 0 ? "bottom-14" : "bottom-3"
        }`}
      >
        <div className="flex flex-wrap gap-1.5">
          <span className={chip} title={category}>
            <span className="max-w-[16rem] truncate">{category}</span>
          </span>
          {team.motor && <span className={chip}>{team.motor}</span>}
          <span className={`${chip} gap-1 tabular-nums`}>
            <Users className="h-3 w-3" aria-hidden="true" />
            {memberCountLabel(team.members.length)}
          </span>
        </div>
        <h3 className="line-clamp-2 text-xl font-semibold leading-tight" title={team.name}>
          {team.name}
        </h3>
        {inst && (
          <p className="truncate text-sm text-white/90" title={inst}>
            {inst}
          </p>
        )}
      </div>

      <TeamRoster team={team} />
    </article>
  )
}
